// ENG-2277: a Form's PDF, and the page count the PDF check shows from it. The PDF library is loaded only
// when a Form's panel asks, so the templates page does not carry it.
import { createElement } from "react";

import type { FormSettings } from "components/pdf-generation-v3/form-print.utils";
import { itemsTablesAsPrinted } from "components/word-processor-field/merge-tags/merge-tags-values/common/items-as-drawn.util";
import { importWithRetry } from "utils/lazy-with-retry.util";

import { countPdfPages } from "./pdf-check.util";

/**
 * What the print dialog adds to a Form's page, each of which can move text onto another page: how it
 * renders tables (its Enhanced Table Rendering switch and the table layout-fix flag) and Suno's
 * signature footer. Left out, the Form is rendered alone, as its settings panel shows it.
 */
export type FormPrintOptions = {
  pdfNativeTablesEnabled?: boolean;
  printableTableLayoutFixesEnabled?: boolean;
  signatureFooter?: boolean;
};

// The footer keeps the same space clear under the form whoever signs, so one unnamed line stands
// for it.
const FOOTER_SIGNATURES = [{ label: "Patient Signature" }];

/**
 * The Form printed as it will be, merge tags unfilled. ENG-2463: an items table kept as the PDF draws it is
 * drawn with its printed rows, not as its tag's text, so its pages are counted at the height it prints.
 */
export const renderFormPdf = async (
  html: string,
  formSettings: FormSettings | null,
  title: string,
  { signatureFooter, ...tableRendering }: FormPrintOptions = {}
): Promise<Blob> => {
  const [{ pdf }, { default: PdfDocument }, footer] = await Promise.all([
    importWithRetry(() => import("@react-pdf/renderer"), "react-pdf-renderer"),
    importWithRetry(
      () => import("components/pdf-generation-v3/pdf-document.component"),
      "pdf-document"
    ),
    signatureFooter
      ? importWithRetry(
          () =>
            import(
              "components/pdf-generation-v3/footer-v3/pdf-document-footer-report-v3.component"
            ),
          "pdf-document-footer-report-v3"
        )
      : null
  ]);
  return pdf(
    createElement(PdfDocument, {
      title,
      pages: [
        {
          bodyHtml: itemsTablesAsPrinted(html),
          formSettings,
          ...tableRendering,
          ...(footer && {
            footer: createElement(footer.default, {
              signatures: FOOTER_SIGNATURES
            })
          })
        }
      ]
    })
  ).toBlob();
};

export const renderPageCount = async (
  html: string,
  formSettings: FormSettings | null,
  print?: FormPrintOptions
): Promise<number> =>
  countPdfPages(
    await (await renderFormPdf(html, formSettings, "Page count", print)).text()
  );
